import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Oval } from "react-loading-icons";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import {
  TableBody,
  TableCell,
  TableRow,
  Table,
  TableHead,
} from "@material-ui/core";
import moment from "moment";
import { eventBus } from "../services/eventBusService";
import { DashboardHeadercrm } from "../cmp/DashboardHeadercrm";
import { crmService } from "../services/crmService";
import { loadUsers, setFilter } from "../store/actions/userAction";
import {
  saveCrm,
  keepSaisie2,
  removeSaisie2,
  updateCrmDateMaj2,
  setEditCrmuSoldCred,
  setEditCrmuSoldDeb,
} from "../store/actions/crmAction";
import { confirmAlert } from "react-confirm-alert";
import { Modal3 } from "./Modal3";

// Custom hook to track window size
function useWindowSize() {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
  useEffect(() => {
    const handleResize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return size;
}

// Utility: sort by descending date
const sortByDateDesc = (arr) =>
  [...arr].sort((a, b) => new Date(b.dateEvent) - new Date(a.dateEvent));

// Utility: sum of debits
const calcDeb = (saisie) =>
  saisie.reduce((sum, { debit }) => sum + Number(debit), 0);

// Utility: sum of credits
const calcCred = (saisie) =>
  saisie.reduce((sum, { credit }) => sum + Number(credit), 0);

// Styled Material‑UI table cells/rows
const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    minWidth: 400,
  },
});

export function HistoDonsCrm() {
  const dispatch = useDispatch();
  const crmIdxEdited = useSelector((s) => s.crmReducer.crmIdxEdited);
  const loggedInUser = useSelector((s) => s.userReducer.loggedInUser);
  const [data, setData] = useState(null);
  const [saisies, setSaisies] = useState([]);
  const [openFlag2, setOpenFlag2] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [flagSauv, setFlagSauv] = useState(false);
  const [flagDeroule, setFlagDeroule] = useState(1);
  // load users once
  useEffect(() => {
    dispatch(loadUsers());
  }, [dispatch]);

  // fetch CRM details when editIndex changes
  const loadCrmAndSaisies = useCallback(async () => {
    if (!crmIdxEdited) return;

    const crm = await crmService.getById(crmIdxEdited);
    setData(crm);

    const saisiesCrm = await keepSaisie2({ crmId: crmIdxEdited });
    setSaisies(saisiesCrm);
  }, [crmIdxEdited]);

  /*useEffect(() => {
    loadCrmAndSaisies();
  }, [loadCrmAndSaisies]);*/

  useEffect(() => {
    if (openFlag2 === false) {
      loadCrmAndSaisies();
    }
  }, [openFlag2, loadCrmAndSaisies]);

  useEffect(() => {
    if (flagSauv === true) {
      loadCrmAndSaisies();
      setFlagSauv(false);
    }
  }, [flagSauv, loadCrmAndSaisies]);

  useEffect(() => {
    // 1. Récupère la hauteur de ligne (en pixels) du document
    if (flagDeroule === 2) {
      const lineHeight = parseFloat(
        getComputedStyle(document.documentElement).lineHeight,
      ); // :contentReference[oaicite:0]{index=0}

      // 2. Descend de 5 fois cette hauteur de ligne, en douceur
      window.scrollBy({
        top: lineHeight * 20,
        behavior: "smooth",
      });
      setFlagDeroule(3);
      // :contentReference[oaicite:1]{index=1}
    }
  }, [flagDeroule]);
  // sorted saisie entries
  const sortedSaisie = useMemo(
    () => (saisies ? sortByDateDesc(saisies) : []),
    [saisies],
  );

  // compute current balance
  const { currentCred, currentDeb } = useMemo(() => {
    if (!saisies) return { currentCred: "0", currentDeb: "0" };
    const totDeb = calcDeb(saisies);
    const totCred = calcCred(saisies);
    if (totCred > totDeb) {
      return {
        currentCred: (totCred - totDeb).toFixed(2).toString(),
        currentDeb: "0",
      };
    }
    return {
      currentCred: "0",
      currentDeb: (totDeb - totCred).toFixed(2).toString(),
    };
  }, [saisies]);

  // update Redux with new balances
  useEffect(() => {
    dispatch(setEditCrmuSoldCred(currentCred));
    dispatch(setEditCrmuSoldDeb(currentDeb));
  }, [dispatch, currentCred, currentDeb]);

  // window size hook
  const [width] = useWindowSize();
  const isWide = width > 700;

  // Handlers
  const onDelete = useCallback(
    (rowIdx) => {
      confirmAlert({
        title: "Confirmation de la suppression",
        message: "Cette action est irréversible. Confirmez-vous ?",
        buttons: [
          {
            label: "Oui",
            onClick: async () => {
              const saisieId = sortedSaisie[rowIdx]?._id;

              if (!saisieId) {
                console.warn("Aucune saisie trouvée pour suppression");
                return;
              }

              await removeSaisie2(saisieId);
              await updateCrmDateMaj2(crmIdxEdited);

              setFlagSauv(true);
            },
          },
          { label: "Non" },
        ],
      });
    },
    [sortedSaisie, crmIdxEdited],
  );

  const onEdit = useCallback((rowIdx) => {
    setEditIndex(rowIdx);
    setOpenFlag2(true);
  }, []);

  const onAdd = useCallback(() => {
    setEditIndex(null);
    setOpenFlag2(true);
  }, []);

  const classes = useStyles();

  if (!data || !loggedInUser) {
    return <Oval className="oval" stroke="#06BCEE" fill="#06BCEE" />;
  }

  return (
    <div>
      {openFlag2 && (
        <Modal3
          isOpen={openFlag2}
          toggle={() => setOpenFlag2(false)}
          data3={sortedSaisie}
          keyData={editIndex}
        />
      )}

      <DashboardHeadercrm
        crmCount={saisies.length}
        firstName={data.firstName}
        lastName={data.lastName}
        onAdd={onAdd}
      />

      <Table className={classes.table} aria-label="Historique des dons">
        <TableHead>
          <tr>
            <th>Évennement</th>
            <th>Date</th>
            <th>Débit (NIS)</th>
            <th>Crédit (NIS)</th>
            {isWide && <th>Mode paiement</th>}
            {isWide && <th>Collecteur</th>}
            {loggedInUser.isManager && <th>Voir</th>}
            {loggedInUser.isManager && <th>Supprimer</th>}
          </tr>
        </TableHead>
        <TableBody>
          {sortedSaisie.map((don, idx) => (
            <StyledTableRow key={don.id || idx}>
              <StyledTableCell>{don.event}</StyledTableCell>
              <StyledTableCell>
                {moment(don.dateEvent).format("DD-MM-YY")}
              </StyledTableCell>
              <StyledTableCell>{don.debit}</StyledTableCell>
              <StyledTableCell
                style={{
                  backgroundColor: don.justificatif ? "#b7f7c1" : "inherit",
                }}
              >
                {don.credit}
              </StyledTableCell>
              {isWide && <StyledTableCell>{don.mode}</StyledTableCell>}
              {isWide && <StyledTableCell>{don.destinataire}</StyledTableCell>}
              {loggedInUser.isManager && (
                <StyledTableCell onClick={() => onEdit(idx)}>
                  <i className="fas fa-eye fa-lg" />
                </StyledTableCell>
              )}
              {loggedInUser.isManager && (
                <StyledTableCell onClick={() => onDelete(idx)}>
                  <i className="fas fa-trash-alt fa-lg" />
                </StyledTableCell>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      {flagDeroule === 1 && setFlagDeroule(2)}
    </div>
  );
}
